'use strict';

define('vb/private/vx/partialDigestExtensionRegistry',[
  'vb/private/vx/extensionRegistry',
  'vb/private/constants',
  'vb/private/utils',
], (ExtensionRegistry, Constants, Utils) => {
  /**
   * @typedef {import('vb/private/vx/extension.js').default} Extension
   */

  class PartialDigestExtensionRegistry extends ExtensionRegistry {
    constructor(registryConfig) {
      super(registryConfig);
      this._digestState = Constants.DigestState.PARTIAL;
      this._fetchPartialManifestPromise = registryConfig.fetchPartialManifestPromise;

      /**
       * Lazily loads the complete manifest when a resource explicitly names an extension that was omitted from
       * the partial digest because of a missing dependency declaration.
       * @type {function(Object=): Array<Promise<Array<Object>>>|null}
       */
      this._loadFullManifest = registryConfig.loadFullManifest;
      this._loadFullManifestPromise = null;
      this._activeFullManifestRecoveryPromise = null;
      // Keep each provider's recovery result so callers for the same provider share the exact promise, while
      // a different provider joining an active recovery can validate its own presence after the registry updates.
      this._fullManifestRecoveryPromisesByExtensionId = Object.create(null);

      // A failed bootstrap partial digest must retain the full-digest fallback used before the registry split.
      // Otherwise _getExtensionPromise() treats the failure as fatal and leaves the application with no extensions.
      this._fetchManifestPromise = this._fetchPartialManifestPromise.catch((error) => {
        this.log.error('Error fetching partial manifest, using the full manifest instead:', error);
        this._digestState = Constants.DigestState.FULL;
        return this._loadFullManifest();
      });
      this._partialDigestPromisesByUrlId = Object.create(null);
      this._partialDigestLoadPromisesByUrlId = Object.create(null);
      this._loadedPartialDigestUrlIds = Object.create(null);
      this._loadedPartialDigestPromises = new WeakSet();
      // Each partial digest carries the extension manager's order for the extensions it contains. Keep those
      // ordering constraints separately from the id map, whose insertion order only reflects navigation history.
      this._partialExtensionOrder = [];
      this._partialExtensionOrderConstraints = Object.create(null);
      // The complete app-ui-info digest supplies the global catalog. Keep the bootstrap partial metadata only
      // as a fallback when that best-effort request is unavailable.
      this._bootstrapPartialAppUiInfo = Object.create(null);
      // Fetching partial digests may happen concurrently, but every manifest application mutates shared registry
      // and RequireJS state. Partial applications and full-manifest recovery must therefore share one queue.
      this._manifestApplyPromise = Promise.resolve();
      // A queued partial application waits for its network response to preserve request order. Signal it as soon
      // as full recovery begins so an unresolved partial fetch cannot block the authoritative full manifest.
      this._createFullManifestRecoveryStartPromise();
      /**
       * App UI URL id for the partial digest supplied during application bootstrap.
       * This tracks the route alias used to fetch the initial partial digest so it can be marked
       * as loaded and released after the digest metadata has been applied.
       * @type {String}
       * @private
       */
      this._initialPartialDigestAppUiUrlId = registryConfig.partialDigestAppUi;

      /**
       * Loads a partial digest for the given App UI URL id when the registry adapter supports it.
       * @type {function(String): Promise<Array<Promise<Array<Object>>>>|null}
      */
      this._loadPartialManifest = registryConfig.loadPartialManifest;

      this._partialDigestPromisesByUrlId[this._initialPartialDigestAppUiUrlId] = this._fetchPartialManifestPromise;
    }

    /**
     * Create the one-shot signal used to release queued partial applications when full recovery starts.
     * @private
     */
    _createFullManifestRecoveryStartPromise() {
      this._fullManifestRecoveryStartPromise = new Promise((resolve) => {
        this._startFullManifestRecovery = resolve;
      });
    }

    async _getLoadManifestPromise() {
      const manifestPromises = await this._loadManifest();
      const [runtimeDigestPromise, requirejsInfoDigestPromise, partialAppUiInfoDigestPromise,
        fullAppUiInfoDigestPromise] = manifestPromises;
      const appUiInfoPromise = this._digestState === Constants.DigestState.PARTIAL
        ? this._getPartialAppUiInfoMap(partialAppUiInfoDigestPromise)
          .then((partialAppUiInfo) => {
            this._bootstrapPartialAppUiInfo = partialAppUiInfo;
            return this._getAppUiInfoMap(fullAppUiInfoDigestPromise);
          })
        : this._getAppUiInfoMap(fullAppUiInfoDigestPromise);

      return [
        runtimeDigestPromise,
        requirejsInfoDigestPromise.then((requirejsInfo) => this._processRequirejsInfoDigest(requirejsInfo)),
        appUiInfoPromise,
      ];
    }

    /**
     * Normalize the app-ui-info portion of the bootstrap partial digest.
     * @param {Promise<Array<Object>>|Array<Object>} partialAppUiInfoDigest
     * @return {Promise<Object>}
     * @private
     */
    _getPartialAppUiInfoMap(partialAppUiInfoDigest) {
      return Promise.resolve(partialAppUiInfoDigest)
        .then((partialAppUiInfo) => this._normalizeAppUiInfoDigest(partialAppUiInfo));
    }

    /**
     * Return the full App UI catalog when available. A partial digest is a subset of that catalog, so retain its
     * bootstrap metadata only as a fallback when the best-effort full app-ui-info request resolves empty. Once
     * recovery switches to the authoritative full manifest, do not reuse bootstrap metadata for extensions that
     * may no longer exist in that manifest.
     * @param {Promise<Array<Object>>|Array<Object>} [fullAppUiInfoDigest] full catalog, including DT overrides
     * @return {Promise<Object>}
     * @private
     */
    async _getAppUiInfoMap(fullAppUiInfoDigest) {
      const fullAppUiInfo = await this._getFullAppUiInfoMap(fullAppUiInfoDigest);
      return Object.keys(fullAppUiInfo).length > 0 || this._digestState === Constants.DigestState.FULL
        ? fullAppUiInfo
        : this._bootstrapPartialAppUiInfo;
    }

    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    _shouldRefreshExistingExtension(refreshExisting) {
      return true;
    }

    /**
     * Merge a newly loaded partial runtime definition with an existing Extension object.
     * @param {Extension} currentExtension
     * @param {Object} definition
     * @return {Object}
     * @private
     */
    _mergeExtensionDefinition(currentExtension, definition) {
      if (!currentExtension) {
        return definition;
      }

      if (definition.version && definition.version !== currentExtension.version) {
        this.log.warn('Extension', definition.id, 'was loaded from partial digests with different versions:',
          currentExtension.version, definition.version);
      }

      return Object.assign({}, definition, {
        version: definition.version || currentExtension.version,
        baseUrl: definition.baseUrl || currentExtension.baseUrlDef,
        dependencies: Object.assign({}, currentExtension.dependencies, definition.dependencies),
        files: Array.from(new Set((currentExtension.files || []).concat(definition.files || []))),
      });
    }

    _getRefreshExtensionDefinition(currentExtension, definition) {
      // Once recovery has upgraded to the full manifest, it is authoritative: do not retain files or
      // dependencies that were only present in an earlier partial digest.
      return this._digestState === Constants.DigestState.FULL
        ? definition
        : this._mergeExtensionDefinition(currentExtension, definition);
    }

    _getAppliedManifestExtensions(manifestExtensions) {
      if (this._digestState === Constants.DigestState.FULL) {
        const manifestExtensionIds = new Set(manifestExtensions.map((extension) => extension.id));
        Object.keys(this._extensions).forEach((extensionId) => {
          if (!manifestExtensionIds.has(extensionId)) {
            delete this._extensions[extensionId];
          }
        });
        this._partialExtensionOrder = manifestExtensions.map((extension) => extension.id);
        this._partialExtensionOrderConstraints = Object.create(null);
        return manifestExtensions;
      }

      return this._mergePartialExtensionOrder(manifestExtensions);
    }

    /**
     * Merge the extension manager order from a partial digest with orders observed from earlier partial digests.
     * Existing order is the tie breaker when two extensions have never appeared together, preserving deterministic
     * request order while allowing a later digest to place an already-loaded extension after its provider.
     * @param {Array<Extension>} manifestExtensions extensions in extension-manager order for the current digest
     * @return {Array<Extension>} accumulated extensions in dependency and override order
     * @private
     */
    _mergePartialExtensionOrder(manifestExtensions) {
      const knownIds = new Set(this._partialExtensionOrder);
      manifestExtensions.forEach((extension, index) => {
        if (!knownIds.has(extension.id)) {
          this._partialExtensionOrder.push(extension.id);
          knownIds.add(extension.id);
        }

        if (index > 0) {
          const previousId = manifestExtensions[index - 1].id;
          const nextIds = this._partialExtensionOrderConstraints[previousId]
            || (this._partialExtensionOrderConstraints[previousId] = new Set());
          nextIds.add(extension.id);
        }
      });

      const extensionIds = this._partialExtensionOrder.filter((extensionId) => this._extensions[extensionId]);
      const extensionIdsSet = new Set(extensionIds);
      const incomingEdges = Object.create(null);
      extensionIds.forEach((extensionId) => {
        incomingEdges[extensionId] = 0;
      });
      Object.entries(this._partialExtensionOrderConstraints).forEach(([extensionId, nextIds]) => {
        if (extensionIdsSet.has(extensionId)) {
          nextIds.forEach((nextId) => {
            if (extensionIdsSet.has(nextId)) {
              incomingEdges[nextId] += 1;
            }
          });
        }
      });

      const orderedIds = [];
      const addedIds = new Set();
      while (orderedIds.length < extensionIds.length) {
        const nextId = extensionIds.find((extensionId) => !addedIds.has(extensionId)
          && incomingEdges[extensionId] === 0);
        if (!nextId) {
          // Extension manager ordering should never be cyclic. Retain the last deterministic order if its input is.
          this.log.warn('Partial manifest has conflicting extension-manager ordering constraints.');
          return extensionIds.map((extensionId) => this._extensions[extensionId]);
        }

        orderedIds.push(nextId);
        addedIds.add(nextId);
        const nextIds = this._partialExtensionOrderConstraints[nextId];
        if (nextIds) {
          nextIds.forEach((followingId) => {
            if (extensionIdsSet.has(followingId)) {
              incomingEdges[followingId] -= 1;
            }
          });
        }
      }

      this._partialExtensionOrder = orderedIds;
      return orderedIds.map((extensionId) => this._extensions[extensionId]);
    }

    _afterInitialManifestApplied() {
      this._loadedPartialDigestPromises.add(this._fetchPartialManifestPromise);
      this._loadedPartialDigestUrlIds[this._initialPartialDigestAppUiUrlId] = true;
      this._releasePartialDigestPromise(this._initialPartialDigestAppUiUrlId);
    }

    /**
     * True while this registry still represents partial extension metadata that can be upgraded to the full manifest.
     * @return {boolean}
     */
    get isPartialDigest() {
      return this._digestState === Constants.DigestState.PARTIAL;
    }

    /**
     * Upgrade the registry to the full manifest once. Callers share the same promise so concurrent resource
     * requests do not issue multiple full digest fetches.
     * @param {String} [missingExtensionId] extension that was omitted because its dependency was not declared
     * @return {Promise<boolean>} true when the registry was upgraded to the full manifest
     */
    loadFullManifest(missingExtensionId) {
      // Once recovery starts, callers must wait for it while the full manifest is fetched and applied.
      if (this._loadFullManifestPromise) {
        // Do not hide a concurrent, distinct missing provider behind the first recovery diagnostic. It needs its
        // own actionable message, while repeat requests for the same provider continue to share one diagnostic.
        if (missingExtensionId && !this._fullManifestRecoveryPromisesByExtensionId[missingExtensionId]) {
          this._logMissingExtensionRecovery(missingExtensionId);
        }
        return this._getFullManifestRecoveryPromise(missingExtensionId);
      }

      if (this._digestState === Constants.DigestState.FULL) {
        return Promise.resolve(false);
      }

      if (!this._loadFullManifest || !Utils.isOnline()) {
        return Promise.resolve(false);
      }

      this._startFullManifestRecovery();
      if (missingExtensionId) {
        this._logMissingExtensionRecovery(missingExtensionId);
      }
      const recoveryOptions = missingExtensionId ? { rejectOnError: true } : undefined;
      this._loadFullManifestPromise = Promise.resolve(this._loadFullManifest(recoveryOptions))
        .then(async (fullManifest) => {
          const [runtimeDigestPromise, requirejsInfoDigestPromise] = fullManifest;
          await Promise.all([runtimeDigestPromise, requirejsInfoDigestPromise]);

          // Do not change the live registry until the strict recovery fetch has completed. Queue the full apply behind
          // any partial apply already in progress: both paths use ConfigLoader's global, non-nested RequireJS
          // configuration deferral. Provider-specific validation happens only after this shared application completes.
          return this._queueManifestApplication(async () => {
            this._fetchManifestPromise = fullManifest;
            this._digestState = Constants.DigestState.FULL;
            this._extensionsPromise = this._getExtensionPromise(true);
            await this._extensionsPromise;
            // Keep the existing AppUiInfos instance because Application retains it, but remove bootstrap partial
            // entries before rebuilding from the authoritative full extension set.
            this._appUiInfos.clear();
            await this.initRegistry();
            return true;
          });
        })
        .catch((error) => {
          this._loadFullManifestPromise = null;
          this._fullManifestRecoveryPromisesByExtensionId = Object.create(null);
          // A failed recovery leaves the partial registry usable. Future partial navigations need a new signal.
          this._createFullManifestRecoveryStartPromise();
          throw error;
        });
      const activeRecoveryPromise = this._loadFullManifestPromise;
      this._activeFullManifestRecoveryPromise = activeRecoveryPromise;
      const clearActiveRecovery = () => {
        if (this._activeFullManifestRecoveryPromise === activeRecoveryPromise) {
          this._activeFullManifestRecoveryPromise = null;
        }
      };
      activeRecoveryPromise.then(clearActiveRecovery, clearActiveRecovery);

      return this._getFullManifestRecoveryPromise(missingExtensionId);
    }

    /**
     * Wait for all manifest work visible at the time of the call. Loop when waiting exposes another queued apply so
     * callers receive a generation that is stable until their next asynchronous boundary.
     * @return {Promise<number>} stable manifest generation
     */
    async whenManifestStable() {
      while (true) {
        const extensionsPromise = this._extensionsPromise;
        const recoveryPromise = this._activeFullManifestRecoveryPromise;
        const applicationPromise = this._manifestApplyPromise;
        // eslint-disable-next-line no-await-in-loop
        await Promise.all([
          extensionsPromise ? extensionsPromise.catch(() => undefined) : undefined,
          recoveryPromise ? recoveryPromise.catch(() => undefined) : undefined,
          applicationPromise,
        ]);

        const nextRecoveryPromise = this._activeFullManifestRecoveryPromise;
        if (this._extensionsPromise === extensionsPromise
          && (!nextRecoveryPromise || nextRecoveryPromise === recoveryPromise)
          && this._manifestApplyPromise === applicationPromise) {
          return this.manifestGeneration;
        }
      }
    }

    /**
     * Log an actionable diagnostic for a provider that the current partial digest omitted.
     * @param {String} missingExtensionId extension that was omitted because its dependency was not declared
     * @private
     */
    _logMissingExtensionRecovery(missingExtensionId) {
      this.log.error('Extension', missingExtensionId, 'is missing from the partial digest. Loading the full '
        + 'extension manifest, which may slow application loading. Declare the dependency to avoid recovery.');
    }

    /**
     * Return the shared recovery result for a missing provider. A full manifest is fetched only once, but each
     * distinct provider must be confirmed after that manifest has been applied.
     * @param {String} [missingExtensionId] extension that was omitted because its dependency was not declared
     * @return {Promise<boolean>}
     * @private
     */
    _getFullManifestRecoveryPromise(missingExtensionId) {
      if (!missingExtensionId) {
        return this._loadFullManifestPromise;
      }

      const recoveryPromise = this._fullManifestRecoveryPromisesByExtensionId[missingExtensionId];
      if (recoveryPromise) {
        return recoveryPromise;
      }

      const validatedRecoveryPromise = this._loadFullManifestPromise.then((recovered) => {
        if (recovered && !this.getExtensionById(missingExtensionId)) {
          throw new Error(`Full manifest recovery did not include extension '${missingExtensionId}'.`);
        }
        return recovered;
      });
      this._fullManifestRecoveryPromisesByExtensionId[missingExtensionId] = validatedRecoveryPromise;
      return validatedRecoveryPromise;
    }

    /**
     * Release a partial digest promise after its metadata has been applied.
     * The loaded URL id marker remains as the cache entry that prevents another network round trip.
     * @param {String} appUiUrlId
     * @private
     */
    _releasePartialDigestPromise(appUiUrlId) {
      delete this._partialDigestPromisesByUrlId[appUiUrlId];
    }

    /**
     * Ensure the partial digest for an App UI has been loaded.
     * @param {String} appUiId canonical App UI id used by navigation
     * @return {Promise<boolean>} true when a new partial digest was loaded
     */
    async loadAppUiDigest(appUiId) {
      // Full-manifest recovery is authoritative. Do not start another partial load while the registry is
      // transitioning to full mode, even though _digestState is not updated until the full digest is available.
      if (this._loadFullManifestPromise) {
        await this._loadFullManifestPromise;
        return false;
      }

      if (this._digestState === Constants.DigestState.FULL) {
        return false;
      }

      if (!this._appUiInfos.exist(appUiId)) {
        return false;
      }

      // Navigation uses the canonical App UI id, but the partial digest endpoint expects
      // the URL alias from the full app-ui-info digest.
      const appUiUrlId = this._appUiInfos.getUrlIdFromAppId(appUiId) || appUiId;

      const inProgress = this._partialDigestLoadPromisesByUrlId[appUiUrlId];
      if (inProgress) {
        return inProgress;
      }

      if (this._loadedPartialDigestUrlIds[appUiUrlId]) {
        return false;
      }

      const loadPromise = this._loadAppUiDigest(appUiUrlId);
      this._partialDigestLoadPromisesByUrlId[appUiUrlId] = loadPromise;
      try {
        return await loadPromise;
      } finally {
        if (this._partialDigestLoadPromisesByUrlId[appUiUrlId] === loadPromise) {
          delete this._partialDigestLoadPromisesByUrlId[appUiUrlId];
        }
      }
    }

    /**
     * Fetch and apply a partial digest for an App UI URL id. Callers use loadAppUiDigest() so concurrent
     * requests share this work rather than applying the same digest more than once.
     * @param {String} appUiUrlId route alias used to fetch the partial digest
     * @return {Promise<boolean>} true when a new partial digest was loaded
     * @private
     */
    async _loadAppUiDigest(appUiUrlId) {
      if (this._partialDigestPromisesByUrlId[appUiUrlId]) {
        await this.getExtensions();
        if (this._loadedPartialDigestUrlIds[appUiUrlId]) {
          return false;
        }
      }

      if (!this._loadPartialManifest && !this._partialDigestPromisesByUrlId[appUiUrlId]) {
        return false;
      }

      let partialDigestPromise = this._partialDigestPromisesByUrlId[appUiUrlId];
      if (!partialDigestPromise) {
        partialDigestPromise = this._loadPartialManifest(appUiUrlId);
        this._partialDigestPromisesByUrlId[appUiUrlId] = partialDigestPromise;
      }

      if (this._loadedPartialDigestPromises.has(partialDigestPromise)) {
        this._loadedPartialDigestUrlIds[appUiUrlId] = true;
        return false;
      }

      const fullManifestRecoveryStartPromise = this._fullManifestRecoveryStartPromise;
      // Use a non-array signal so a recovery can be distinguished from the digest tuple returned by Promise.all().
      const recoveryStarted = Symbol('full-manifest-recovery-started');
      const recoveryStartedPromise = fullManifestRecoveryStartPromise.then(() => recoveryStarted);
      return this._queueManifestApplication(async () => {
        const manifestPromises = await Promise.race([
          partialDigestPromise,
          recoveryStartedPromise,
        ]);
        if (!Array.isArray(manifestPromises)) {
          return false;
        }

        const [runtimeDigestPromise, requirejsInfoDigestPromise] = manifestPromises;
        const digests = await Promise.race([
          Promise.all([
            runtimeDigestPromise,
            requirejsInfoDigestPromise,
          ]),
          recoveryStartedPromise,
        ]);
        if (!Array.isArray(digests)) {
          return false;
        }
        const [extArray, requirejsInfoDigest] = digests;

        // A full recovery may have started while this request was in flight. Its manifest must remain the
        // authoritative registry state. Do not await it here: recovery's full apply is queued behind this
        // callback, so waiting would deadlock the queue.
        if (this._loadFullManifestPromise) {
          return false;
        }

        if (this._digestState === Constants.DigestState.FULL) {
          return false;
        }

        const requirejsInfo = this._processRequirejsInfoDigest(requirejsInfoDigest);
        const appUiInfo = await this._getAppUiInfoMap();
        this.log.info('Found', extArray.length, 'extension(s) in the partial manifest for App UI', appUiUrlId);
        const extensions = await this._applyManifest(extArray, requirejsInfo, appUiInfo, true);
        this._extensionsPromise = Promise.resolve(extensions);
        this._loadedPartialDigestPromises.add(partialDigestPromise);
        this._loadedPartialDigestUrlIds[appUiUrlId] = true;
        this._releasePartialDigestPromise(appUiUrlId);
        await this.updateAppUiInfos();
        return true;
      }).catch((error) => {
        this._releasePartialDigestPromise(appUiUrlId);
        this.log.error('Error fetching partial manifest for App UI', appUiUrlId, error);
        throw error;
      });
    }

    /**
     * Serialize manifest applications while allowing partial-digest network fetches to overlap.
     * @param {function(): Promise<boolean>} callback applies one partial or full manifest
     * @return {Promise<boolean>} result of the queued manifest application
     * @private
     */
    _queueManifestApplication(callback) {
      const applicationPromise = this._manifestApplyPromise.then(callback, callback);
      // Continue processing later manifest applications after a failed application.
      this._manifestApplyPromise = applicationPromise.catch(() => undefined);
      return applicationPromise;
    }

    async updateAppUiInfos(extensions) {
      try {
        const runtimeEnv = await Utils.getRuntimeEnvironment();
        // For previewing root page, DT needs to disable App UIs
        const disableAppUis = await runtimeEnv.disableAppUis();
        // App UIs are disabled by setting _appUiInfos to an empty appUiInfos so that
        // when getAppUiInfos() is called, an AppUIInfos object without any App UI is returned.
        if (disableAppUis === true) {
          this._appUiInfos.clear();
          return;
        }

        if (!extensions) {
          await this.getExtensions();
        }
        const appUiInfo = await this._getAppUiInfoMap();
        this._addAppUiInfosFromDigest(appUiInfo);
      } catch (err) {
        // Initialize with an empty AppUiInfos so that the application can still run, but no App UI will be loaded
        this._appUiInfos.clear();

        // Swallow the error so that it doesn't break the application, but no App UI will be loaded
        this.log.error('Error updating App UI Infos, no App UI will be available', err);
      }
    }
  }

  return PartialDigestExtensionRegistry;
});

